/**
 * YouTube URL 검증 유틸리티
 *
 * 클라이언트 사이드 정규식 패턴을 사용하여 YouTube URL을 검증하고 비디오 ID를 추출합니다.
 * 표준 YouTube URL 형식과 Shorts URL을 지원하며 지원하지 않는 패턴(채널)을 차단합니다.
 */

/**
 * YouTube 비디오 ID 길이 상수 (11자)
 */
const VIDEO_ID_LENGTH = 11;

/**
 * 지원하는 YouTube URL 패턴
 * 각 패턴은 11자 비디오 ID를 캡처합니다
 * - STANDARD/EMBED/SHORTS: 그룹 1에서 캡처
 * - SHORT/MOBILE: 그룹 1에서 캡처
 *
 * 정규식 경계 조건:
 * - STANDARD/MOBILE: videoId 뒤에 &, #, 또는 문자열 종료만 허용
 * - SHORT/EMBED/SHORTS: videoId 뒤에 /, ?, #, 또는 문자열 종료만 허용
 *
 * 프로토콜(http/https)은 선택적이며, www로 시작하는 URL도 허용합니다
 */
export const YOUTUBE_URL_PATTERNS = {
  STANDARD: new RegExp(
    `^(?:https?:\\/\\/)?(?:www\\.)?youtube\\.com\\/watch\\?v=([a-zA-Z0-9_-]{${VIDEO_ID_LENGTH}})(?=[&#]|$)`,
  ),
  SHORT: new RegExp(
    `^(?:https?:\\/\\/)?youtu\\.be\\/([a-zA-Z0-9_-]{${VIDEO_ID_LENGTH}})(?=[/?#]|$)`,
  ),
  MOBILE: new RegExp(
    `^(?:https?:\\/\\/)?m\\.youtube\\.com\\/watch\\?v=([a-zA-Z0-9_-]{${VIDEO_ID_LENGTH}})(?=[&#]|$)`,
  ),
  EMBED: new RegExp(
    `^(?:https?:\\/\\/)?(?:www\\.)?youtube\\.com\\/embed\\/([a-zA-Z0-9_-]{${VIDEO_ID_LENGTH}})(?=[/?#]|$)`,
  ),
  SHORTS: new RegExp(
    `^(?:https?:\\/\\/)?(?:www\\.|m\\.)?youtube\\.com\\/shorts\\/([a-zA-Z0-9_-]{${VIDEO_ID_LENGTH}})(?=[/?#]|$)`,
  ),
  LIVE: new RegExp(
    `^(?:https?:\\/\\/)?(?:www\\.|m\\.)?youtube\\.com\\/live\\/([a-zA-Z0-9_-]{${VIDEO_ID_LENGTH}})(?=[/?#]|$)`,
  ),
};

/**
 * 지원하지 않는 YouTube URL 패턴
 * 이 패턴들은 명시적으로 거부됩니다
 * m.youtube.com 하위 도메인도 포함합니다
 * 프로토콜은 선택적입니다
 */
export const UNSUPPORTED_PATTERNS = {
  CHANNEL: /(?:^|(?:https?:\/\/)?)(?:m\.)?youtube\.com\/(?:channel|c|@)/,
};

/**
 * 검증 결과 인터페이스
 * isValid가 true일 때는 videoId가 존재하고, false일 때는 error가 존재합니다
 */
export interface ValidationResult {
  /** 검증 성공 여부 */
  isValid: boolean;
  /** 추출된 비디오 ID (검증 성공 시) */
  videoId?: string;
  /** 에러 타입 (검증 실패 시) */
  error?: 'INVALID_URL' | 'CHANNEL_URL';
}

/**
 * XSS 방지 패턴
 * 스크립트 태그를 포함한 URL 차단
 */
const XSS_PATTERN = /<script/i;

/**
 * YouTube URL에서 비디오 ID를 추출합니다
 *
 * @param url - YouTube URL 문자열
 * @returns 11자 비디오 ID 또는 찾지 못한 경우 null
 */
export function extractVideoId(url: string): string | null {
  // 지원하는 각 패턴을 순회하며 매칭 시도
  for (const pattern of Object.values(YOUTUBE_URL_PATTERNS)) {
    const match = url.match(pattern);
    if (match) {
      // SHORT/MOBILE 패턴은 그룹 1, STANDARD/EMBED 패턴은 그룹 2에서 videoId 추출
      return match[1];
    }
  }

  return null;
}

/**
 * YouTube URL을 검증하고 비디오 ID를 추출합니다
 *
 * @param url - 검증할 URL 문자열
 * @returns 검증 결과 (isValid, videoId, error 포함)
 *
 * @example
 * validateYoutubeUrl('https://www.youtube.com/watch?v=dQw4w9WgXcQ')
 * // 반환: { isValid: true, videoId: 'dQw4w9WgXcQ' }
 *
 * @example
 * validateYoutubeUrl('https://youtube.com/shorts/dQw4w9WgXcQ')
 * // 반환: { isValid: true, videoId: 'dQw4w9WgXcQ' }
 */
export function validateYoutubeUrl(url: string): ValidationResult {
  // 1. 빈 문자열 체크
  if (!url || url.trim() === '') {
    return {
      isValid: false,
      error: 'INVALID_URL',
    };
  }

  // 2. 프로토콜 검증 (http/https만 허용, 프로토콜 없으면 https 추가)
  try {
    const trimmedUrl = url.trim();
    // 프로토콜이 없으면 https:// 추가
    const urlToValidate = trimmedUrl.match(/^https?:\/\//)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
    const parsedUrl = new URL(urlToValidate);
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return {
        isValid: false,
        error: 'INVALID_URL',
      };
    }
  } catch {
    return {
      isValid: false,
      error: 'INVALID_URL',
    };
  }

  // 3. XSS 방지 - 스크립트 태그 차단
  if (XSS_PATTERN.test(url)) {
    return {
      isValid: false,
      error: 'INVALID_URL',
    };
  }

  // 4. 지원하지 않는 패턴 먼저 체크 (조기 반환)

  if (UNSUPPORTED_PATTERNS.CHANNEL.test(url)) {
    return {
      isValid: false,
      error: 'CHANNEL_URL',
    };
  }

  // 5. 지원하는 패턴에서 비디오 ID 추출 시도
  const videoId = extractVideoId(url);

  if (!videoId) {
    return {
      isValid: false,
      error: 'INVALID_URL',
    };
  }

  // 6. 성공 - 비디오 ID 반환
  return {
    isValid: true,
    videoId,
  };
}
